import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { openTopLevelDialog } from '~/components/CmsMain/globals';
import { extractError } from '~/Utils/errors/extractError';

import { signalSorClaimLockChanged } from './sorClaimLockSignal';
import { endClaimWrite, getSorClaimPage, startClaimWrite } from './sorClaimWrites';
import { getSorErrorCode, getSorErrorTitle, isRequestTimeout, markSorHandled } from './sorErrors';
import TEXTS from './texts';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** A request the user did not make, such as the touch CMS sends on opening a claim: no SoR dialog for it. */
    sorSilent?: boolean;
  }
}

export const SOR_REQUEST_TIMEOUT_MS = 45_000;

const READ_METHODS = new Set(['get', 'head', 'options']);
// A route that names its claim by id, as the server's claim freeze reads it.
const CLAIM_URL = /\/claims\/\d+(?:[/?]|$)/;

const isClaimWrite = (config: AxiosRequestConfig | undefined): config is AxiosRequestConfig =>
  config !== undefined && !READ_METHODS.has((config.method ?? 'get').toLowerCase()) && CLAIM_URL.test(config.url ?? '');

const sorName = () => getSorClaimPage()?.sorName ?? TEXTS.defaultSorName;

/**
 * Install the one place a claim write waits on the organization's System of Record, while a claim page of an
 * organization with the SoR flag is open. Such a write gets its own timeout, past which the change is unconfirmed,
 * and no retry, since a resent change could be made twice. The claim page counts the seconds it waits. A file upload
 * keeps the browser's own pace: it is not an SoR operation, and a large one outlasts the timeout.
 *
 * A claim write answered with an SoR outcome opens the dialog titled by it, or the unconfirmed dialog on a timeout,
 * and the error is marked handled so the caller's own report does not show a second popup. The error is rejected
 * either way, so the dialog that sent it stops submitting as before.
 */
export function installSorInterceptor(axiosInstance: AxiosInstance): {
  requestInterceptorId: number;
  responseInterceptorId: number;
} {
  const requestInterceptorId = axiosInstance.interceptors.request.use((config) => {
    if (getSorClaimPage() === null || !isClaimWrite(config) || config.data instanceof FormData) {
      return config;
    }
    const sorConfig = { ...config, timeout: SOR_REQUEST_TIMEOUT_MS, 'axios-retry': { retries: 0 } };
    startClaimWrite(sorConfig);
    // A cancelled request rejects with no config to say which one it was.
    config.cancelToken?.promise.then(() => endClaimWrite(sorConfig));
    return sorConfig;
  });

  const responseInterceptorId = axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      endClaimWrite(response.config);
      return response;
    },
    (error: AxiosError) => {
      const sentAt = error.config ? endClaimWrite(error.config) : null;
      const timedOut = sentAt !== null && isRequestTimeout(error);
      const errorCode = getSorErrorCode(error);
      if (errorCode === null && !timedOut) {
        return Promise.reject(error);
      }
      signalSorClaimLockChanged();
      if (isClaimWrite(error.config) && !error.config.sorSilent) {
        const { title, message } = timedOut
          ? { title: getSorErrorTitle('sor_unconfirmed'), message: TEXTS.requestTimedOut(sorName()) }
          : extractError(error);
        openTopLevelDialog({ title, message });
        markSorHandled(error);
      }
      return Promise.reject(error);
    }
  );

  return { requestInterceptorId, responseInterceptorId };
}
